import { solarToLunar as convertSolarToLunar, lunarToSolar as convertLunarToSolar } from 'lunar-calendar'

/**
 * 农历/公历互转工具。
 *
 * 口径说明（与星盘后端 /fate 返回的农历格式一致）：
 * - 展示格式：`干支年，月名，日名`，如 丙午年，正月初一（前端统一为半角逗号）
 * - 月名：正月…十月、冬月(11)、腊月(12)，闰月加「闰」前缀
 * - 日名：初一…初十、十一…十九、二十、廿一…廿九、三十
 *
 * 支持的输入写法：
 * 1) 丙午年,正月初一 / 丙午年正月初一 / 闰六月初二
 * 2) 正月初一 / 腊月三十（省略年份时，取锚点日期所在的农历年）
 * 3) 2026-5-1 / 2026年5月1日（数字年月日，按农历解释）
 * 4) 二〇二六年正月初一（中文数字年）
 *
 * 注意：lunar-calendar 的干支年按「立春」切换，而农历月属于农历年，
 * 因此立春到春节之间同一天文日期会出现「干支年已换、农历年未换」的情况，
 * 导致干支→农历年存在歧义；本文件通过「多候选 + 按输入文本回验 + 就近锚点日期」消除歧义。
 * 支持范围为 lunar-calendar 的 1891-2100 年。
 */

const MIN_YEAR = 1891
const MAX_YEAR = 2100

const GAN = '甲乙丙丁戊己庚辛壬癸'
const ZHI = '子丑寅卯辰巳午未申酉戌亥'

const MONTH_NUMBER_BY_NAME = {
  正: 1,
  一: 1,
  二: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
  十: 10,
  冬: 11,
  十一: 11,
  腊: 12,
  十二: 12
}

const DAY_NAMES = [
  '', '初一', '初二', '初三', '初四', '初五', '初六', '初七', '初八', '初九', '初十',
  '十一', '十二', '十三', '十四', '十五', '十六', '十七', '十八', '十九', '二十',
  '廿一', '廿二', '廿三', '廿四', '廿五', '廿六', '廿七', '廿八', '廿九', '三十'
]

const CHINESE_DIGITS = { '〇': 0, 零: 0, 一: 1, 二: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9 }

const GAN_ZHI_PATTERN = '[甲乙丙丁戊己庚辛壬癸][子丑寅卯辰巳午未申酉戌亥]'
const MONTH_PATTERN = '闰?[正一二三四五六七八九十冬腊]{1,2}月'
const DAY_PATTERN = '.{1,3}'

/** 统一全角/半角与空白，便于解析与比较 */
function normalizeText (rawText) {
  if (rawText === null || rawText === undefined) {
    return ''
  }
  return String(rawText)
    .replace(/[\uFF0C\u3001]/g, ',')
    .replace(/\uFF1A/g, ':')
    .replace(/[，、]/g, ',')
    .replace(/农历/g, '')
    .replace(/\s+/g, '')
    .trim()
}

/** 去掉所有分隔符，用于「输入文本」与「重新生成的文本」比较 */
function normalizeTextForCompare (rawText) {
  return normalizeText(rawText).replace(/[,\-/.年月日]/g, '')
}

/** 锚点：支持数字年份或 { year, month, day } */
function normalizeAnchor (anchor) {
  if (anchor === null || anchor === undefined) {
    return null
  }
  if (typeof anchor === 'number') {
    return Number.isFinite(anchor) && anchor > 0 ? { year: anchor, month: 0, day: 0 } : null
  }
  if (typeof anchor === 'object') {
    const year = Number(anchor.year)
    const month = Number(anchor.month) || 0
    const day = Number(anchor.day) || 0
    return Number.isFinite(year) && year > 0 ? { year, month, day } : null
  }
  return null
}

/** 候选日期与锚点的距离（天）；锚点缺月日时退化为按年比较 */
function anchorDistanceDays (solar, anchor) {
  if (!anchor) {
    return 0
  }
  if (!anchor.month || !anchor.day) {
    return Math.abs(solar.year - anchor.year) * 365
  }
  const anchorTime = Date.UTC(anchor.year, anchor.month - 1, anchor.day)
  const solarTime = Date.UTC(solar.year, solar.month - 1, solar.day)
  return Math.abs(anchorTime - solarTime) / 86400000
}

function chineseYearToNumber (text) {
  if (!/^[〇零一二三四五六七八九]{4}$/.test(text)) {
    return 0
  }
  let result = ''
  for (const char of text) {
    if (!(char in CHINESE_DIGITS)) {
      return 0
    }
    result += String(CHINESE_DIGITS[char])
  }
  return Number(result)
}

/** 由农历年份取干支，如 1984 → 甲子 */
export function ganZhiOfYear (year) {
  const numericYear = Number(year)
  if (!Number.isFinite(numericYear) || numericYear <= 0) {
    return ''
  }
  const ganIndex = ((numericYear - 4) % 10 + 10) % 10
  const zhiIndex = ((numericYear - 4) % 12 + 12) % 12
  return GAN[ganIndex] + ZHI[zhiIndex]
}

/**
 * 干支 → 候选农历年（60 年一循环，按与锚点年份的接近程度排序）
 * @param {string} ganZhi 如「丙午」
 * @param {number|object} anchor 锚点年份或日期
 */
export function lunarYearsByGanZhi (ganZhi, anchor) {
  const target = String(ganZhi || '').trim()
  if (target.length !== 2) {
    return []
  }
  const anchorDate = normalizeAnchor(anchor)
  const anchorYear = anchorDate ? anchorDate.year : new Date().getFullYear()
  const candidates = []
  for (let year = MIN_YEAR; year <= MAX_YEAR; year++) {
    if (ganZhiOfYear(year) === target) {
      candidates.push(year)
    }
  }
  candidates.sort((left, right) => Math.abs(left - anchorYear) - Math.abs(right - anchorYear))
  return candidates
}

/** 解析月名（五月 / 闰五月 / 冬月 / 腊月） */
function parseMonthName (rawMonthName) {
  const name = String(rawMonthName || '').replace(/月$/, '')
  const isLeap = name.startsWith('闰')
  const core = isLeap ? name.slice(1) : name
  if (core in MONTH_NUMBER_BY_NAME) {
    return { monthNumber: MONTH_NUMBER_BY_NAME[core], isLeap }
  }
  return null
}

/** 解析日名（初一…三十），兼容阿拉伯数字 */
function parseDayName (rawDayName) {
  const name = String(rawDayName || '').replace(/日$/, '')
  const index = DAY_NAMES.indexOf(name)
  if (index > 0) {
    return index
  }
  const numericDay = Number(name)
  if (Number.isFinite(numericDay) && numericDay >= 1 && numericDay <= 30) {
    return numericDay
  }
  return 0
}

/**
 * 解析农历文本，返回 { lunarYear, ganZhiCandidates?, monthNumber, isLeap, day, displayFormat }
 * 无法解析时返回 null。
 */
export function parseLunarDateText (rawText, anchor) {
  const text = normalizeText(rawText)
  if (!text) {
    return null
  }

  // 1) 干支年 + 月名 + 日名（展示格式）
  const ganZhiMatch = new RegExp(`^(${GAN_ZHI_PATTERN})年?,?(${MONTH_PATTERN}),?(${DAY_PATTERN})$`).exec(text)
  if (ganZhiMatch) {
    const monthInfo = parseMonthName(ganZhiMatch[2])
    const day = parseDayName(ganZhiMatch[3])
    const years = lunarYearsByGanZhi(ganZhiMatch[1], anchor)
    if (!monthInfo || !day || years.length === 0) {
      return null
    }
    return {
      lunarYear: years[0],
      ganZhiCandidates: years,
      ganZhi: ganZhiMatch[1],
      monthNumber: monthInfo.monthNumber,
      isLeap: monthInfo.isLeap,
      day,
      displayFormat: true
    }
  }

  // 2) 中文数字年 + 月名 + 日名
  const chineseYearMatch = new RegExp(`^([〇零一二三四五六七八九]{4})年?(${MONTH_PATTERN}),?(${DAY_PATTERN})$`).exec(text)
  if (chineseYearMatch) {
    const year = chineseYearToNumber(chineseYearMatch[1])
    const monthInfo = parseMonthName(chineseYearMatch[2])
    const day = parseDayName(chineseYearMatch[3])
    if (!year || !monthInfo || !day) {
      return null
    }
    return { lunarYear: year, monthNumber: monthInfo.monthNumber, isLeap: monthInfo.isLeap, day, displayFormat: false }
  }

  // 3) 数字年月日（按农历解释）
  const numericMatch = /^(\d{3,4})[年\-/.](\d{1,2})[月\-/.](\d{1,2})日?$/.exec(text)
  if (numericMatch) {
    return {
      lunarYear: Number(numericMatch[1]),
      monthNumber: Number(numericMatch[2]),
      isLeap: false,
      day: Number(numericMatch[3]),
      displayFormat: false
    }
  }

  // 4) 只有月名 + 日名：年份取锚点日期所在的农历年
  const monthDayMatch = new RegExp(`^(${MONTH_PATTERN}),?(${DAY_PATTERN})$`).exec(text)
  if (monthDayMatch) {
    const monthInfo = parseMonthName(monthDayMatch[1])
    const day = parseDayName(monthDayMatch[2])
    const anchorDate = normalizeAnchor(anchor)
    if (!monthInfo || !day || !anchorDate) {
      return null
    }
    const anchorLunar = convertSolarToLunar(anchorDate.year, anchorDate.month || 6, anchorDate.day || 1)
    if (!anchorLunar || anchorLunar.error) {
      return null
    }
    return {
      lunarYear: anchorLunar.lunarYear,
      monthNumber: monthInfo.monthNumber,
      isLeap: monthInfo.isLeap,
      day,
      displayFormat: false
    }
  }

  return null
}

/**
 * 在 lunar-calendar 的「含闰月月序号(1-13)」体系里定位目标月序号。
 * 通过往返校验确认序号与「月名 + 是否闰月」一致，避免闰月错位。
 */
function resolveLunarMonthIndex (lunarYear, monthNumber, isLeap) {
  for (let index = 1; index <= 13; index++) {
    const solar = convertLunarToSolar(lunarYear, index, 1)
    if (!solar || solar.error) {
      continue
    }
    const back = convertSolarToLunar(solar.year, solar.month, solar.day)
    if (!back || back.error) {
      continue
    }
    const backIsLeap = String(back.lunarMonthName || '').startsWith('闰')
    const backMonth = parseMonthName(back.lunarMonthName)
    if (backMonth && backMonth.monthNumber === monthNumber && backIsLeap === isLeap) {
      return index
    }
  }
  return 0
}

/** 校验候选日期是否与解析结果一致（展示格式用文本回验，其余用农历三元组回验） */
function verifyCandidate (solar, parsed, normalizedInput) {
  const back = convertSolarToLunar(solar.year, solar.month, solar.day)
  if (!back || back.error) {
    return false
  }
  if (parsed.displayFormat && normalizedInput) {
    return normalizeTextForCompare(solarDateToLunarText(solar.year, solar.month, solar.day)) === normalizedInput
  }
  const backIsLeap = String(back.lunarMonthName || '').startsWith('闰')
  const backMonth = parseMonthName(back.lunarMonthName)
  return Boolean(backMonth) &&
    back.lunarYear === parsed.lunarYear &&
    backMonth.monthNumber === parsed.monthNumber &&
    backIsLeap === parsed.isLeap &&
    back.lunarDay === parsed.day
}

/**
 * 农历文本 → 公历日期
 * @param {string} rawText 农历文本（用户输入）
 * @param {number|object} anchor 锚点：年份数字或 { year, month, day }（用于干支定年与就近择一）
 * @returns {{year:number, month:number, day:number}|null}
 */
export function lunarDateTextToSolarDate (rawText, anchor) {
  const normalizedInput = normalizeTextForCompare(rawText)
  const parsed = parseLunarDateText(rawText, anchor)
  if (!parsed || !normalizedInput) {
    return null
  }
  const anchorDate = normalizeAnchor(anchor)

  // 展示格式：干支年按立春切换，可能与农历年差一年；故对每组干支候选再取 ±1 年一起验证
  const baseYears = Array.isArray(parsed.ganZhiCandidates) && parsed.ganZhiCandidates.length > 0
    ? parsed.ganZhiCandidates
    : [parsed.lunarYear]
  const yearPool = []
  baseYears.forEach(year => {
    const expansion = parsed.displayFormat ? [year - 1, year, year + 1] : [year]
    expansion.forEach(item => {
      if (item >= MIN_YEAR && item <= MAX_YEAR && !yearPool.includes(item)) {
        yearPool.push(item)
      }
    })
  })

  const candidates = []
  yearPool.forEach(lunarYear => {
    const monthIndex = resolveLunarMonthIndex(lunarYear, parsed.monthNumber, parsed.isLeap)
    if (!monthIndex) {
      return
    }
    const solar = convertLunarToSolar(lunarYear, monthIndex, parsed.day)
    if (!solar || solar.error) {
      return
    }
    if (parsed.displayFormat) {
      // 展示格式：以「文本回验」为准（含立春歧义的两侧都会命中）
      const regeneratedText = normalizeTextForCompare(solarDateToLunarText(solar.year, solar.month, solar.day))
      if (regeneratedText === normalizedInput) {
        candidates.push(solar)
        return
      }
      // 兼容按「农历年干支」书写的输入：干支按立春切换，春节前会出现
      // 「农历年已是乙巳、包/后端仍输出甲辰」的情况，这里补一条按农历年干支的匹配。
      const back = convertSolarToLunar(solar.year, solar.month, solar.day)
      if (!back || back.error) {
        return
      }
      const backIsLeap = String(back.lunarMonthName || '').startsWith('闰')
      const backMonth = parseMonthName(back.lunarMonthName)
      if (parsed.ganZhi &&
          ganZhiOfYear(back.lunarYear) === parsed.ganZhi &&
          backMonth && backMonth.monthNumber === parsed.monthNumber &&
          backIsLeap === parsed.isLeap &&
          back.lunarDay === parsed.day) {
        candidates.push(solar)
      }
      return
    }
    if (verifyCandidate(solar, parsed, normalizedInput)) {
      candidates.push(solar)
    }
  })

  if (candidates.length === 0) {
    return null
  }
  candidates.sort((left, right) => anchorDistanceDays(left, anchorDate) - anchorDistanceDays(right, anchorDate))
  const best = candidates[0]
  return { year: best.year, month: best.month, day: best.day }
}

/** 公历日期 → 农历文本（与后端格式一致：丙午年,正月初一） */
export function solarDateToLunarText (year, month, day) {
  const lunar = convertSolarToLunar(year, month, day)
  if (!lunar || lunar.error) {
    return ''
  }
  return `${lunar.GanZhiYear}年,${lunar.lunarMonthName},${lunar.lunarDayName}`
}

/** 公历日期对象 → 表单使用的 YYYY/MM/DD 文本 */
export function formatSolarDateText (date) {
  if (!date || !Number.isFinite(date.year) || !Number.isFinite(date.month) || !Number.isFinite(date.day)) {
    return ''
  }
  const pad = value => String(value).padStart(2, '0')
  return `${String(date.year).padStart(4, '0')}/${pad(date.month)}/${pad(date.day)}`
}

/** 解析表单公历文本，得到 { year, month, day }；解析失败返回 null */
export function parseSolarDateText (rawText) {
  const text = String(rawText || '').trim()
  const match = /(-?\d{1,6})\s*[-/.]\s*(\d{1,2})\s*[-/.]\s*(\d{1,2})/.exec(text)
  if (!match) {
    return null
  }
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null
  }
  return { year, month, day }
}

/** 解析表单里的公历文本，取年份用于干支定年；解析失败返回 0 */
export function resolveSolarYearFromText (rawText) {
  const parsed = parseSolarDateText(rawText)
  if (parsed) {
    return parsed.year
  }
  const match = /(-?\d{1,6})/.exec(String(rawText || '').trim())
  if (!match) {
    return 0
  }
  const year = Number(match[1])
  return Number.isFinite(year) ? year : 0
}

/**
 * 将公历日期转换为农历日期
 * @param {string} solarDate - 公历日期，格式为 'YYYY-MM-DD'
 * @returns {string} 农历日期字符串，格式为 '农历YYYY年MM月DD日'
 */
export function solarToLunar (solarDate) {
  const date = new Date(solarDate)
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()

  const lunar = convertSolarToLunar(year, month, day)
  if (!lunar || lunar.error) {
    return ''
  }
  return `${lunar.lunarYear}年${lunar.lunarMonth}月${lunar.lunarDay}日`
}

/**
 * 将农历日期转换为公历日期
 * @param {string} lunarDate - 农历日期字符串，格式为 '农历YYYY年MM月DD日'
 * @returns {string} 公历日期，格式为 'YYYY-MM-DD'
 */
export function lunarToSolar (lunarDate) {
  const matches = RegExp(/(\d+)年(\d+)月(\d+)日/).exec(String(lunarDate || ''))
  if (!matches) return ''

  const lunarYear = parseInt(matches[1], 10)
  const lunarMonth = parseInt(matches[2], 10)
  const lunarDay = parseInt(matches[3], 10)

  const solar = convertLunarToSolar(lunarYear, lunarMonth, lunarDay)
  if (!solar || solar.error) {
    return ''
  }

  return `${solar.year}-${String(solar.month).padStart(2, '0')}-${String(solar.day).padStart(2, '0')}`
}

export const LUNAR_SUPPORTED_YEAR_RANGE = { min: MIN_YEAR, max: MAX_YEAR }
